import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GlobalButton } from '../components/GlobalButton';
import GlobalSidebar from '../components/GlobalSidebar';
import {
  RealEstatePropertyForm,
  type RealEstatePropertyFormValues,
} from '../components/RealEstatePropertyForm';
import {
  EntityDocumentsPanel,
  type EntityDocumentUploadControl,
} from '../components/EntityDocumentsPanel';
import { ProjectGanttView } from '../components/ProjectGanttView';
import { RealEstateChatPanel } from '../components/RealEstateChatPanel';
import { StatCard } from '../components/StatCard';
import { ViewLayoutToggle } from '../components/ViewLayoutToggle';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { ViewSections, type ViewSection } from '../components/ViewSections';
import { useViewLayoutMode } from '../hooks/useViewLayoutMode';
import { useAppData } from '../context/AppDataContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { propertyEquity, realEstateTypeLabel } from '../lib/realEstate';
import {
  deleteRealEstateProperty,
  saveRealEstateProperty,
} from '../lib/saveRealEstateProperty';
import { formatDateLong, formatEuro } from '../utils/formatters';
import { Favicon, normalizeWebsiteUrl } from '../utils/favicon';
import type { RealEstateProjectPhase, RealEstateProjectTask } from '../types';
import authStyles from '../pages/auth/authPages.module.css';
import styles from './RealEstateDetailView.module.css';
import '../App.css';

function equityValueNode(equity: number) {
  const className =
    equity > 0
      ? 'financialSnapshot-profit financialSnapshot-profit--positive'
      : equity < 0
        ? 'financialSnapshot-profit financialSnapshot-profit--negative'
        : '';
  return <span className={className}>{formatEuro(equity)}</span>;
}

export function RealEstateDetailView() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { realEstateItems, realEstateLoading } = useAppData();
  const { mode: layoutMode, toggle: toggleLayout } = useViewLayoutMode();
  const userId = user?.uid ?? '';

  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [documentUploadControl, setDocumentUploadControl] =
    useState<EntityDocumentUploadControl | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [projectSaving, setProjectSaving] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);

  const property = useMemo(
    () => (realEstateItems ?? []).find((item) => item.id === propertyId) ?? null,
    [realEstateItems, propertyId]
  );

  const projectPhases = useMemo(() => property?.projectPhases ?? [], [property]);
  const projectTasks = useMemo(() => property?.projectTasks ?? [], [property]);

  async function handleSave(values: RealEstatePropertyFormValues) {
    if (!user?.uid || !property || editSaving) return;

    setEditError(null);
    setEditSaving(true);

    try {
      await saveRealEstateProperty(user.uid, {
        ...values,
        id: property.id,
        existing: { createdAt: property.createdAt },
      });
      setEditOpen(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to save property.');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDelete() {
    if (!user?.uid || !property || deleting) return;

    setDeleteError(null);
    setDeleting(true);

    try {
      await deleteRealEstateProperty(user.uid, property.id);
      navigate('/real-estate');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete property.');
      setDeleting(false);
    }
  }

  const handleSavePhase = useCallback(
    async (phase: RealEstateProjectPhase) => {
      if (!user?.uid || !property || projectSaving) return;

      setProjectError(null);
      setProjectSaving(true);

      try {
        const existingIndex = projectPhases.findIndex((p) => p.id === phase.id);
        const updatedPhases =
          existingIndex >= 0
            ? projectPhases.map((p) => (p.id === phase.id ? phase : p))
            : [...projectPhases, phase];

        await saveRealEstateProperty(user.uid, {
          ...property,
          id: property.id,
          projectPhases: updatedPhases,
          projectTasks,
          existing: {
            createdAt: property.createdAt,
            projectPhases: updatedPhases,
            projectTasks,
          },
        });
      } catch (err) {
        setProjectError(err instanceof Error ? err.message : 'Failed to save phase.');
      } finally {
        setProjectSaving(false);
      }
    },
    [user?.uid, property, projectPhases, projectTasks, projectSaving]
  );

  const handleDeletePhase = useCallback(
    async (phaseId: string) => {
      if (!user?.uid || !property || projectSaving) return;

      setProjectError(null);
      setProjectSaving(true);

      try {
        const updatedPhases = projectPhases.filter((p) => p.id !== phaseId);
        const updatedTasks = projectTasks.filter((t) => t.phaseId !== phaseId);

        await saveRealEstateProperty(user.uid, {
          ...property,
          id: property.id,
          projectPhases: updatedPhases,
          projectTasks: updatedTasks,
          existing: {
            createdAt: property.createdAt,
            projectPhases: updatedPhases,
            projectTasks: updatedTasks,
          },
        });
      } catch (err) {
        setProjectError(err instanceof Error ? err.message : 'Failed to delete phase.');
      } finally {
        setProjectSaving(false);
      }
    },
    [user?.uid, property, projectPhases, projectTasks, projectSaving]
  );

  const handleSaveTask = useCallback(
    async (task: RealEstateProjectTask) => {
      if (!user?.uid || !property || projectSaving) return;

      setProjectError(null);
      setProjectSaving(true);

      try {
        const existingIndex = projectTasks.findIndex((t) => t.id === task.id);
        const updatedTasks =
          existingIndex >= 0
            ? projectTasks.map((t) => (t.id === task.id ? task : t))
            : [...projectTasks, task];

        await saveRealEstateProperty(user.uid, {
          ...property,
          id: property.id,
          projectPhases,
          projectTasks: updatedTasks,
          existing: {
            createdAt: property.createdAt,
            projectPhases,
            projectTasks: updatedTasks,
          },
        });
      } catch (err) {
        setProjectError(err instanceof Error ? err.message : 'Failed to save task.');
      } finally {
        setProjectSaving(false);
      }
    },
    [user?.uid, property, projectPhases, projectTasks, projectSaving]
  );

  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      if (!user?.uid || !property || projectSaving) return;

      setProjectError(null);
      setProjectSaving(true);

      try {
        const updatedTasks = projectTasks.filter((t) => t.id !== taskId);

        await saveRealEstateProperty(user.uid, {
          ...property,
          id: property.id,
          projectPhases,
          projectTasks: updatedTasks,
          existing: {
            createdAt: property.createdAt,
            projectPhases,
            projectTasks: updatedTasks,
          },
        });
      } catch (err) {
        setProjectError(err instanceof Error ? err.message : 'Failed to delete task.');
      } finally {
        setProjectSaving(false);
      }
    },
    [user?.uid, property, projectPhases, projectTasks, projectSaving]
  );

  const handleToggleTask = useCallback(
    (taskId: string) => {
      const task = projectTasks.find((t) => t.id === taskId);
      if (!task) return;

      const updatedTask: RealEstateProjectTask = {
        ...task,
        done: !task.done,
        completedAt: task.done ? null : new Date().toISOString(),
      };

      handleSaveTask(updatedTask);
    },
    [projectTasks, handleSaveTask]
  );

  const handleBulkImport = useCallback(
    async (newPhases: RealEstateProjectPhase[], newTasks: RealEstateProjectTask[]) => {
      if (!user?.uid || !property || projectSaving) return;

      setProjectError(null);
      setProjectSaving(true);

      try {
        const updatedPhases = [...projectPhases, ...newPhases];
        const updatedTasks = [...projectTasks, ...newTasks];

        await saveRealEstateProperty(user.uid, {
          ...property,
          id: property.id,
          projectPhases: updatedPhases,
          projectTasks: updatedTasks,
          existing: {
            createdAt: property.createdAt,
            projectPhases: updatedPhases,
            projectTasks: updatedTasks,
          },
        });
      } catch (err) {
        setProjectError(err instanceof Error ? err.message : 'Failed to import phases.');
      } finally {
        setProjectSaving(false);
      }
    },
    [user?.uid, property, projectPhases, projectTasks, projectSaving]
  );

  if (realEstateLoading) {
    return (
      <ViewPage title="Property" backTo="/real-estate" backLabel="Back to real estate">
        <section>
          <p className="muted">Loading real estate…</p>
        </section>
      </ViewPage>
    );
  }

  if (!propertyId || !property) {
    return (
      <ViewPage title="Property" backTo="/real-estate" backLabel="Back to real estate">
        <section>
          <p className="muted">Property not found.</p>
        </section>
      </ViewPage>
    );
  }

  const equity = propertyEquity(property);
  const pencilIcon = (
    <img
      src={isDarkMode ? '/assets/icons/pencil.svg' : '/assets/icons/pencil.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  const websiteHref = normalizeWebsiteUrl(property.websiteUrl);
  let websiteHost: string | null = null;
  if (websiteHref) {
    try {
      websiteHost = new URL(websiteHref).hostname;
    } catch {
      websiteHost = null;
    }
  }

  const headerSubtitle = [realEstateTypeLabel(property.propertyType), property.address]
    .filter(Boolean)
    .join(' · ');

  const toolbarActions = (
    <>
      <ViewLayoutToggle mode={layoutMode} onToggle={toggleLayout} />
      <GlobalButton
        type="button"
        variant="secondary"
        className={styles.tapTarget}
        onClick={() => {
          setEditError(null);
          setEditOpen(true);
        }}
        icon={pencilIcon}
      >
        Edit property
      </GlobalButton>
    </>
  );

  const detailsSection = (
    <>
      <ViewSectionHeader eyebrow="Real estate" title={property.name} subtitle={headerSubtitle}>
        {property.websiteUrl || websiteHost ? (
          <div className="companyCell" style={{ marginTop: 8 }}>
            {property.websiteUrl ? (
              <Favicon
                websiteUrl={property.websiteUrl}
                size={20}
                alt=""
                className="companyFavicon"
              />
            ) : null}
            {websiteHref && websiteHost ? (
              <a
                href={websiteHref}
                target="_blank"
                rel="noopener noreferrer"
                className="detailWebsiteLink"
              >
                {websiteHost}
              </a>
            ) : null}
          </div>
        ) : null}
      </ViewSectionHeader>

      <section className="grid-stats grid-stats-2" aria-label="Property summary">
        <StatCard
          netWorthHeadline
          label="Net equity value"
          emoji="🏠"
          value={equityValueNode(equity)}
          hint="Market value minus mortgage"
          valueWrap
        />
        <StatCard
          label="Market value"
          emoji="📊"
          value={formatEuro(property.currentValue)}
          hint={realEstateTypeLabel(property.propertyType)}
          valueWrap
        />
        <StatCard
          label="Mortgage"
          emoji="📉"
          value={formatEuro(property.mortgageBalance ?? 0)}
          hint="Outstanding balance"
          valueWrap
        />
        <StatCard
          label="Monthly rent"
          emoji="💶"
          value={formatEuro(property.monthlyRent ?? 0)}
          hint="Gross rental income"
          valueWrap
        />
      </section>

      <ViewSectionHeader eyebrow="Real estate" title="Details" subtitle="Acquisition and value info." />

      <section className="grid-stats grid-stats-2" aria-label="Property details">
        <StatCard
          label="Purchase price"
          emoji="🏷️"
          value={
            typeof property.purchasePrice === 'number'
              ? formatEuro(property.purchasePrice)
              : '—'
          }
          valueWrap
        />
        <StatCard
          label="Purchase date"
          emoji="📅"
          value={formatDateLong(property.purchaseDate)}
          valueWrap
        />
        <StatCard
          label="Appreciation"
          emoji="📈"
          value={
            typeof property.appreciationRate === 'number'
              ? `${property.appreciationRate}% p.a.`
              : '—'
          }
          valueWrap
        />
        <StatCard
          label="Last updated"
          emoji="🕒"
          value={formatDateLong(property.valueUpdatedAt ?? property.updatedAt)}
          valueWrap
        />
      </section>

      {property.notes ? (
        <>
          <ViewSectionHeader eyebrow="Real estate" title="Notes" />
          <section className="table-card">
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{property.notes}</p>
          </section>
        </>
      ) : null}

      <ViewSectionHeader eyebrow="Real estate" title="Manage" subtitle="Remove this property." />

      <section className={styles.dangerZone}>
        {deleteError ? (
          <div
            className={`${authStyles.banner} ${authStyles.bannerError} ${styles.banner}`}
            role="alert"
          >
            {deleteError}
          </div>
        ) : null}
        {!deleteConfirmOpen ? (
          <GlobalButton
            type="button"
            variant="secondary"
            destructive
            className={styles.tapTarget}
            disabled={deleting}
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Delete property
          </GlobalButton>
        ) : (
          <div className={styles.deleteActions}>
            <p className="muted" style={{ margin: 0 }}>
              Are you sure? This cannot be undone.
            </p>
            <GlobalButton
              type="button"
              variant="secondary"
              destructive
              className={styles.tapTarget}
              isLoading={deleting}
              disabled={deleting}
              onClick={handleDelete}
            >
              Confirm delete
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="secondary"
              className={styles.tapTarget}
              disabled={deleting}
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </GlobalButton>
          </div>
        )}
      </section>
    </>
  );

  const documentsSection = userId ? (
    <>
      <ViewSectionHeader
        eyebrow="Real estate"
        title="Documents"
        subtitle={`Files attached to ${property.name} — indexed for intelligence search.`}
        actions={
          <GlobalButton
            type="button"
            variant="secondary"
            isLoading={documentUploadControl?.uploading}
            disabled={documentUploadControl?.uploading}
            onClick={() => documentUploadControl?.openFilePicker()}
            icon={
              <img
                src={
                  isDarkMode
                    ? '/assets/icons/icloud.and.arrow.up.svg'
                    : '/assets/icons/icloud.and.arrow.up.black.svg'
                }
                alt=""
                width={16}
                height={16}
              />
            }
          >
            Upload document
          </GlobalButton>
        }
      />
      <EntityDocumentsPanel
        userId={userId}
        entityType="real_estate"
        entityId={property.id}
        entityLabel={property.name}
        onUploadControlChange={setDocumentUploadControl}
      />
    </>
  ) : (
    <p className="muted">Sign in to manage documents.</p>
  );

  const projectSection = (
    <ProjectGanttView
      phases={projectPhases}
      tasks={projectTasks}
      saving={projectSaving}
      error={projectError}
      onSavePhase={handleSavePhase}
      onDeletePhase={handleDeletePhase}
      onSaveTask={handleSaveTask}
      onDeleteTask={handleDeleteTask}
      onToggleTask={handleToggleTask}
      onBulkImport={handleBulkImport}
    />
  );

  const chatSection = userId ? (
    <>
      <ViewSectionHeader
        eyebrow="Real estate"
        title="Chat"
        subtitle={`Ask questions about ${property.name} — valuation, financing, rental yield, maintenance.`}
      />
      <RealEstateChatPanel
        userId={userId}
        propertyId={property.id}
        propertyName={property.name}
      />
    </>
  ) : (
    <p className="muted">Sign in to chat about this property.</p>
  );

  const sections: ViewSection[] = [
    { id: 'details', label: 'Details', content: detailsSection },
    { id: 'project', label: 'Project', content: projectSection },
    { id: 'documents', label: 'Documents', content: documentsSection },
    { id: 'chat', label: 'Chat', content: chatSection },
  ];

  return (
    <ViewPage
      title={property.name}
      backTo="/real-estate"
      backLabel="Back to real estate"
      actions={toolbarActions}
    >
      <ViewSections mode={layoutMode} sections={sections} />

      <GlobalSidebar
        isOpen={editOpen}
        onClose={() => {
          if (!editSaving) setEditOpen(false);
        }}
      >
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Edit property</h2>
          <p className="bankProviderConnectIntro">
            Update the value, mortgage, rent, and details for this property.
          </p>
          <RealEstatePropertyForm
            key={property.id}
            property={property}
            saving={editSaving}
            error={editError}
            submitLabel="Save changes"
            onSubmit={handleSave}
            onCancel={() => {
              if (!editSaving) setEditOpen(false);
            }}
          />
        </div>
      </GlobalSidebar>
    </ViewPage>
  );
}
